import React, { useEffect, useRef } from 'react';
import { BackButton, Input, ErrorAlert, LengthOfStay, Button, ErrorMessage } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { ApiError, ProviderMapper } from '../Components/Combined';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { REHAB as modelType } from '../Constants/modelTypes';
import useFiscalYear from '../Hooks/useFiscalYear';
import { useNavigate, useLocation } from 'react-router-dom';
import useSessionState from '../Hooks/useSessionState';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, getDefaultDateString } from '../Utils/stringUtils';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { mapToSetters } from '../Utils/mapping';
import { enableAsyncPricingApi } from '../Utils/features';
import usePricerApi from '../Hooks/usePricerApi';

export default function IPRehab() {
    const location = useLocation();
    const { prePopulatedMedicareId, prePopulatedNPI } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [providerName, setProviderName] = useSessionState('', 'providerName');
    const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
    const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
    const [cmg, setCMG] = useSessionState('', 'cmg');
    const [dischargeStatus, setDischargeStatus] = useSessionState('', 'dischargeStatus');
    const [admitDate, setAdmitDate] = useSessionState(getDefaultDateString(), 'admitDate');
    const [dischargeDate, setDischargeDate] = useSessionState(getDefaultDateString(), 'dischargeDate');
    const [fiscalYear, setFiscalYearInputDate] = useFiscalYear(null);
    const [billedAmountTotal, setBilledAmountTotal] = useSessionState('', 'billedAmountTotal');
    const [nonCoveredDays, setNonCoveredDays] = useSessionState(0, 'nonCoveredDays');
    const navigate = useNavigate();

    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);
    const warmupPricer = useAsyncPricerApi(modelType, true);

    const fileExportRef = useRef(null);

    const [fileExporting, fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        "claimId": claimId,
        "providerName": providerName,
        "medicareId": medicareId,
        "npi": npi,
        "cmg": cmg,
        "dischargeStatus": dischargeStatus,
        "admitDate": admitDate,
        "dischargeDate": dischargeDate,
        "billedAmountTotal": billedAmountTotal,
        "nonCoveredDays": nonCoveredDays,
    }, fileExportRef);

    const handleFileSave = () => {
        pricer.reset();
        fileExportExecute();
    };

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const handleFileImportOnChange = (e) => {
        pricer.reset();
        fileImportOnChange(e);
    }

    const handleSubmit = async () => {
        fileExportReset();
        fileImportReset();

        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(fiscalYear, formatRequest());
        }
    };

    const formatRequest = () => {
        return {
            claimId: claimId || null,
            medicareId: medicareId || null,
            paymentMethodology: "FFS",
            fromDate: formatDateStringIfMatches(admitDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toDate: formatDateStringIfMatches(dischargeDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            dischargeStatus: dischargeStatus || null,
            nonCoveredDays: nonCoveredDays || 0,
            serviceLineItems: [
                {
                    lineNumber: 1,
                    hcpcs: cmg || null,
                    revCode: '0024',
                    billed: isNaN(billedAmountTotal) ? 0 : Number(billedAmountTotal)
                }
            ]
        };
    }

    const handleReset = () => {
        setClaimId('');
        setProviderName('');
        setMedicareId('');
        setNpi('');
        setDischargeStatus('');
        setAdmitDate(getDefaultDateString());
        setDischargeDate(getDefaultDateString());
        setBilledAmountTotal('');
        setNonCoveredDays(0);
        pricer.reset();
        fileExportReset();
        fileImportReset();
    };

    useEffect(() => {
        if (fileImportSuccess) {
            const setters = {
                "claimId": setClaimId,
                "providerName": setProviderName,
                "medicareId": setMedicareId,
                "npi": setNpi,
                "cmg": setCMG,
                "dischargeStatus": setDischargeStatus,
                "admitDate": setAdmitDate,
                "dischargeDate": setDischargeDate,
                "billedAmountTotal": setBilledAmountTotal,
                "nonCoveredDays": setNonCoveredDays,
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        setFiscalYearInputDate(dischargeDate);
    }, [dischargeDate]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    result: pricer.result,
                    fiscalYear
                }
            });
        }
    }, [pricer.success]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>IP Rehab Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>
            {(pricer.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            <form noValidate={true}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <ProviderMapper inputs={[
                        {
                            name: "providerName",
                            label: "Provider Name",
                            superscript: "1",
                            value: providerName,
                            onChange: setProviderName
                        },
                        {
                            name: "medicareId",
                            label: "Medicare ID",
                            superscript: "1",
                            value: medicareId,
                            onChange: setMedicareId
                        },
                        {
                            name: "npi",
                            label: "NPI",
                            sublabel: "(facilities may have multiple NPIs)",
                            superscript: "1",
                            value: npi,
                            onChange: setNpi
                        }
                    ]} model={modelType} />
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="CMG (Case Mix Group)" labelClassName="fw-bold" id="cmg" onChange={setCMG} value={cmg} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Discharge Status" labelClassName="fw-bold" id="discharge-status" onChange={setDischargeStatus} value={dischargeStatus} placeholder="00" maxLength={2} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Admit Date" preservePlaceholder={true} labelClassName="fw-bold" id="admit-date" type="date" onChange={setAdmitDate} value={admitDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Discharge Date" preservePlaceholder={true} labelClassName="fw-bold" id="discharge-date" type="date" onChange={setDischargeDate} value={dischargeDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3 align-self-center mt-4">
                        <LengthOfStay fromDate={admitDate} toDate={dischargeDate} nonCoveredDays={nonCoveredDays} />
                    </div>
                    <div className="col-md-3 align-self-center mt-4 fw-bold">
                        {fiscalYear !== null && <>FY{fiscalYear}</>}
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Billed Amount - Total" labelClassName="fw-bold" id="billed-amount-total" onChange={setBilledAmountTotal} value={billedAmountTotal} placeholder="0000.00" />
                    </div>
                    <div className="col-md-3">
                        <Input label="Non-Covered Days" labelClassName="fw-bold" id="non-covered-days" type="number" onChange={setNonCoveredDays} value={nonCoveredDays} />
                    </div>
                </div>
                <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton className="btn-outline-secondary" id="load-request-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onChange={handleFileImportOnChange}
                                    loading={fileImporting}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={pricer.error || fileImportError || fileExportError} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" id="rest-button"
                                    onClick={handleReset}
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    text="Reset" />
                            </div>
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" id="submit-button"
                                    onClick={handleSubmit}
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    text="Submit"
                                    loading={pricer.loading} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
